import React from "react";
import {
  FaFire,
  FaLeaf,
  FaCoffee,
  FaMugHot,
  FaTint,
  FaBroom,
  FaBalanceScale,
  FaEye,
  FaConciergeBell,
} from "react-icons/fa";
import SettingsSectionShell, {
  SettingsSubgroupHeader,
} from "./SettingsSectionShell";
import SettingsFieldCard from "./SettingsFieldCard";
import valuesEqual from "./hooks/valuesEqual";

const COLOR = "emerald";

export const SUBGROUPS = [
  {
    id: "teaFilterCoffee",
    label: "Çay-Filtre Kahve Ortak",
    icon: FaFire,
  },
  { id: "tea", label: "Çay", icon: FaLeaf },
  { id: "filterCoffee", label: "Filtre Kahve", icon: FaCoffee },
  { id: "turkishCoffee", label: "Türk Kahvesi", icon: FaMugHot },
];

// Bazı alt gruplar tek listede okunamayacak kadar kalabalık (Ortak = 14 alan).
// Bunlar konu başlıklarına bölünür: hangi anahtarın hangi başlığa gireceği
// burada tanımlıdır. Listede olmayan (yeni firmware'den gelen) anahtar sona
// "Diğer" başlığı altında düşer — hiçbir ayar sessizce kaybolmaz.
// Başlıklar makinedeki menü adlarıyla BİREBİR aynıdır (kullanıcı kuralı):
// teknisyen panelde ve makine ekranında aynı ismi görmeli. Karşılıklar
// BeverageMachine `src/locale/tr/translation.json` dosyasından:
//   Heater → "Isıtıcı" · TankSettingMenu → "Kazan Ayarı"
//   BrewingSettingMenu → "Demleme Ayarı" · CleaningSettingMenu → "Temizlik Ayarı"
//   GlassSettingMenu → "Bardak Ayarı" · ScreenSetting → "Görüntü Ayarları"
// Yeni başlık eklerken önce makinedeki menü adına bak, yeni isim uydurma.
export const TOPICS = {
  teaFilterCoffee: [
    {
      id: "heater",
      label: "Isıtıcı Ayarı",
      icon: FaFire,
      keys: [
        "heatValue",
        "autoHeaterOpenInterval",
        "autoHeaterControlDegreeWithProgram",
      ],
    },
    {
      id: "tank",
      label: "Kazan Ayarı",
      icon: FaTint,
      keys: [
        "waterTankActive",
        "waterTankPumpMode",
        "potTankVolume",
        "tankDrainValves",
      ],
    },
    {
      id: "brewing",
      label: "Demleme Ayarı",
      icon: FaMugHot,
      keys: ["brewWaterTransitionTime"],
    },
    {
      id: "cleaning",
      label: "Temizlik Ayarı",
      icon: FaBroom,
      keys: ["drain", "cleaning"],
    },
    {
      id: "glass",
      label: "Bardak Ayarı",
      icon: FaBalanceScale,
      keys: ["cups", "massCalibrationThreshold"],
    },
    {
      id: "screen",
      label: "Görüntü Ayarları",
      icon: FaEye,
      keys: ["hideBrewRatio", "hideProductGrams"],
    },
  ],
  // Çay kırılımı kullanıcının verdiği listeye göre (2026-09-01).
  // "Çay Servis Değeri" listede geçmiyordu ama adı gereği Servis Ayarı'na
  // konuldu — aksi halde tek başına "Diğer"e düşüyordu.
  tea: [
    {
      id: "teaBrewing",
      label: "Çay Demleme Ayarı",
      icon: FaMugHot,
      keys: [
        "teaBrewing",
        "teaStaling",
        "teaFirstWater",
        "teaContinueWater",
        "teaWaitingTime",
      ],
    },
    {
      id: "teaCleaning",
      label: "Çay Temizlik Ayarı",
      icon: FaBroom,
      keys: ["cleaningEndCounterTea", "cleaningTimeTea"],
    },
    {
      id: "teaService",
      label: "Çay Servis Ayarı",
      icon: FaConciergeBell,
      keys: ["referenceCup", "teaAbsurb", "teaServiceValue"],
    },
  ],
  // Filtre Kahve, Çay ile birebir simetrik kırılım (aynı alanların filtre
  // kahve karşılıkları). Referans bardak sayısı yalnızca çayda var.
  filterCoffee: [
    {
      id: "filterBrewing",
      label: "Filtre Kahve Demleme Ayarı",
      icon: FaMugHot,
      keys: [
        "filterCoffeeBrewing",
        "filterCoffeeStaling",
        "filterCoffeeFirstWater",
        "filterCoffeeContinueWater",
        "filterCoffeeWaitingTime",
      ],
    },
    {
      id: "filterCleaning",
      label: "Filtre Kahve Temizlik Ayarı",
      icon: FaBroom,
      keys: ["cleaningEndCounterFilterCoffee", "cleaningTimeFilterCoffee"],
    },
    {
      id: "filterService",
      label: "Filtre Kahve Servis Ayarı",
      icon: FaConciergeBell,
      keys: ["filterAbsurb", "filterCoffeeServiceValue"],
    },
  ],
};

// Bardak kayıtları kartının BAŞLIĞINA gömülen alanlar: ayrı kart olarak
// basılmazlar. Ağırlık kalibrasyon eşiği bardak toleranslarıyla birlikte
// ayarlanır — teknisyen aynı kutuda görmeli (kullanıcı isteği).
const CUPS_HEADER_KEYS = ["massCalibrationThreshold"];

// Alt grubun anahtarlarını konu bloklarına böler. Konu tanımı yoksa tek blok
// döner (başlıksız) — davranış eskisiyle aynı kalır.
const splitIntoTopics = (subId, keys) => {
  const topics = TOPICS[subId];
  if (!topics) return [{ id: "all", label: null, icon: null, keys }];
  const claimed = new Set();
  const blocks = topics
    .map((t) => {
      const tKeys = t.keys.filter((k) => {
        if (!keys.includes(k)) return false;
        claimed.add(k);
        return true;
      });
      return { ...t, keys: tKeys };
    })
    .filter((t) => t.keys.length > 0);
  const rest = keys.filter((k) => !claimed.has(k));
  if (rest.length > 0) {
    blocks.push({ id: "other", label: "Diğer", icon: null, keys: rest });
  }
  // Tek blok kaldıysa başlık gürültü olur.
  if (blocks.length === 1) return [{ ...blocks[0], label: null }];
  return blocks;
};

// Konu başlığı — alt grup başlığının (SettingsSubgroupHeader) bir alt seviyesi.
export const TopicHeader = ({ icon: Icon, title, count }) => (
  <div className="flex items-center gap-1.5 mb-1.5 mt-3 first:mt-0">
    {Icon && <Icon className="w-3 h-3 text-emerald-500 shrink-0" />}
    <span className="text-[11px] font-SemiBold text-gray-500">{title}</span>
    <span className="text-[11px] text-gray-400">({count})</span>
    <div className="flex-1 h-px bg-gray-100 ml-1" />
  </div>
);

export default function GeneralSection({ data, onChange, originalData }) {
  if (!data) return null;
  const activeSubs = SUBGROUPS.filter(
    (s) => data[s.id] && Object.keys(data[s.id]).length > 0,
  );
  if (activeSubs.length === 0) return null;
  const totalCount = activeSubs.reduce(
    (sum, s) => sum + Object.keys(data[s.id]).length,
    0,
  );

  return (
    <SettingsSectionShell
      icon={FaFire}
      title="Genel Ayarlar"
      subtitle="Ürün bazlı operasyonel parametreler"
      count={totalCount}
      color={COLOR}
    >
      {activeSubs.map((sub) => {
        const subData = data[sub.id];
        const allKeys = Object.keys(subData);
        // Bardak kartı varsa eşik değeri onun başlığına taşınır, listeden düşer.
        const headerKeys = subData.cups
          ? CUPS_HEADER_KEYS.filter((k) => subData[k])
          : [];
        const subKeys = allKeys.filter((k) => !headerKeys.includes(k));
        return (
          <div key={sub.id}>
            <SettingsSubgroupHeader
              icon={sub.icon}
              title={sub.label}
              count={allKeys.length}
              color={COLOR}
            />
            {splitIntoTopics(sub.id, subKeys).map((topic) => (
              <div key={topic.id} className="mb-4 last:mb-0">
                {topic.label && (
                  <TopicHeader
                    icon={topic.icon}
                    title={topic.label}
                    count={topic.keys.length}
                  />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {topic.keys.map((k) => {
                    const setting = subData[k];
                    const orig = originalData?.[sub.id]?.[k]?.value;
                    const isChanged =
                      orig !== undefined && !valuesEqual(setting.value, orig);
                    return (
                      <SettingsFieldCard
                        key={k}
                        setting={setting}
                        onChange={(value) =>
                          onChange({
                            group: `general.${sub.id}`,
                            key: k,
                            value,
                          })
                        }
                        // Bardak kayıtları kartına "Varsayılana Dön" aksiyonu —
                        // makine fabrika listesini kendisi uygular (panel değer
                        // göndermez, sadece resetCupsToDefault=true kuyruklar).
                        onResetDefaults={
                          setting?.type === "TeaFilterCups"
                            ? () =>
                                onChange({
                                  group: `general.${sub.id}`,
                                  key: "resetCupsToDefault",
                                  value: true,
                                })
                            : undefined
                        }
                        inlineFields={
                          k === "cups"
                            ? headerKeys.map((hk) => ({
                                key: hk,
                                setting: subData[hk],
                                onChange: (value) =>
                                  onChange({
                                    group: `general.${sub.id}`,
                                    key: hk,
                                    value,
                                  }),
                                isChanged:
                                  originalData?.[sub.id]?.[hk]?.value !==
                                    undefined &&
                                  !valuesEqual(
                                    subData[hk]?.value,
                                    originalData[sub.id][hk].value,
                                  ),
                                originalValue:
                                  originalData?.[sub.id]?.[hk]?.value,
                              }))
                            : undefined
                        }
                        isChanged={isChanged}
                        originalValue={orig}
                        color={COLOR}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </SettingsSectionShell>
  );
}
